/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, Component, ElementRef, InputSignal, OnInit, input } from '@angular/core';
import { LogService } from '@app/core/services/log.service';
import { BaseInputComponent } from '@app/modules/inputs/components/base-input/base-input.component';
import { InputsUtilitiesService } from '@app/modules/inputs/services/inputs-utilities.service';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'fdx-input-checkbox',
    providers: [{
        provide: BaseInputComponent,
        useExisting: InputCheckboxComponent
    }],
    templateUrl: './input-checkbox.component.html',
    styleUrl: './input-checkbox.component.scss',
    standalone: false
})
export class InputCheckboxComponent extends BaseInputComponent implements OnInit, AfterViewInit {

    /**
     * Required when using controlValue. Provides a unique id for each checkbox in the group,
     * since multiple checkboxes will share the same controlName (formControlName).
     */
    readonly controlId: InputSignal<string> = input<string>();

    /**
     * When set, activates multi-select mode. This checkbox will add/remove its controlValue
     * from a string[] held by the shared form control, rather than writing a boolean.
     */
    readonly controlValue: InputSignal<string> = input<string>();

    get id(): string {
        return this.controlId() ?? this.controlName;
    }

    constructor(
        protected readonly element: ElementRef<HTMLElement>,
        protected readonly inputsUtilitiesService: InputsUtilitiesService,
        protected readonly logService: LogService,
        protected readonly ngbDateParserFormatter: NgbDateParserFormatter
    ) {
        super(element, inputsUtilitiesService, logService, ngbDateParserFormatter);
        this.inputType = 'checkbox';
    }

    ngOnInit(): void {
        if (this.classes['btn-check']) {
            this.initClasses['form-check-input'] = false;
        } else if (this.classes['form-check-input'] === undefined) {
            this.initClasses['form-check-input'] = true;
        }
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        if (this.controlValue() !== undefined) {
            this.validateConfiguration();
        }
    }

    setValue(value: string | string[] | boolean | null): void {
        if (this.controlValue() !== undefined) {
            const currentArray: string[] = Array.isArray(value) ? value : [];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.value = (currentArray.includes(this.controlValue()) ? this.controlValue() : null) as any;
            this.valueWritten$.next();
            return;
        }
        super.setValue(value as string | null);
    }

    onChange($event: string): void {
        if (this.controlValue() !== undefined) {
            const currentArray: string[] = Array.isArray(this.control.value) ? [...(this.control.value as string[])] : [];
            const idx: number = currentArray.indexOf(this.controlValue());
            if ($event) {
                if (idx === -1) {
                    currentArray.push(this.controlValue());
                }
            } else if (idx !== -1) {
                currentArray.splice(idx, 1);
            }
            this.formField.handleChange(currentArray);
            this.onTouched();
            return;
        }
        super.onChange($event);
        this.onTouched();   // Automatically trigger touch when changed
    }

    validateConfiguration(): string[] {
        const errors: string[] = [];

        if (this.controlId() === undefined) {
            errors.push('binding a controlId of type string is required for InputCheckboxComponent when using controlValue');
        }

        errors.forEach((error: string) => {
            this.logService.error(error);
        });

        return errors;
    }
}
